import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { map, catchError, filter } from 'rxjs/operators';
import { Router } from '@angular/router';
import { CookieOptions, CookieService } from 'ngx-cookie-service';
import { RaygunService } from './raygun.service';

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private translations$ = new BehaviorSubject<Map<string, string>>(new Map());
  private currentLanguage = '1045';
  private mainLanguages = ['1045', '1041', '1153'];
  private missingTranslations = new Set<string>();
  private webAppLabelPrefix = 'WebApp.v1.';
  private appLabelPrefix = 'App.';

  constructor(
    private http: HttpClient,
    private router: Router,
    private cookieService: CookieService,
    private raygunService: RaygunService
  ) {
    const isUnsubscribeRoute = this.router.url.includes('unsubscribe');
    const isManagerUnsubscribeRoute = this.router.url.includes('manager-unsubscribe');

    if (isUnsubscribeRoute || isManagerUnsubscribeRoute) {
      return;
    }

    this.loadTranslations();
  }

  setTranslations(translations: Map<string, string>): void {
    this.translations$.next(translations);
  }

  getTranslation(key: string): Observable<string> {
    return this.translations$.pipe(
      filter(translations => translations && translations.size > 0),
      map(translations => {
        const alreadyTranslated = !key.startsWith(this.webAppLabelPrefix) && !key.startsWith(this.appLabelPrefix);

        if (alreadyTranslated || translations.size === 0) {
          return key;
        }

        const translation = translations.get(key);

        if (!translation) {
          this.reportMissingTranslation(key);
        }

        return translation || key;
      })
    );
  }

  translate(key: string, params?: { [key: string]: string }): string {
    const alreadyTranslated = !key.includes(this.webAppLabelPrefix) && !key.includes(this.appLabelPrefix);

    if (alreadyTranslated || this.translations$.value.size === 0) {
      return key;
    }

    let translation = this.translations$.value.get(key);

    if (!translation) {
      this.reportMissingTranslation(key);

      return key;
    }

    if (params) {
      for (const [paramKey, paramValue] of Object.entries(params)) {
        translation = translation.replace(`{{${paramKey}}}`, paramValue);
      }
    }

    return translation;
  }

  async refreshTranslationsFromApi(language: string): Promise<void> {
    this.currentLanguage = language;
    this.setLanguageCookie(language);
    this.translations$.next(new Map());
    const newTranslations = await this.loadTranslationsFromApi().toPromise();
    this.translations$.next(newTranslations ?? new Map());
  }

  setAuthLanguage(languageId: number): void {
    this.currentLanguage = languageId.toString();
    this.loadTranslations();
  }

  getLanguageId(): string {
    const cookie = this.cookieService.get('languageId');

    if (!!cookie && /^\d+$/.test(cookie) && this.currentLanguage !== cookie) {
      this.currentLanguage = cookie;
    }

    return this.currentLanguage || cookie;
  }

  getLanguageCode(languageId?: number): string {
    const languageIdToUse = languageId ?? +this.getLanguageId();

    switch (languageIdToUse) {
      case 1045: return 'en';
      case 1041: return 'dk';
      case 1153: return 'se';
      case 1058: return 'de';
      default:   return 'dk';
    }
  }

  getMonthLanguageCode(languageId?: number): string {
    const languageIdToUse = languageId ?? +this.getLanguageId();

    switch (languageIdToUse) {
      case 1045: return 'en-GB';
      case 1041: return 'da-DK';
      case 1153: return 'sv-SE';
      case 1058: return 'de-DE';
      default:   return 'en-GB';
    }
  }

  private loadTranslations(): void {
    const isLoginRoute = this.router.url.includes('login');
    const isSignupRoute = this.router.url.includes('signup');
    const isForgotRoute = this.router.url.includes('forgot');
    const isAuthRoute = isLoginRoute || isSignupRoute || isForgotRoute;

    const source$ = isAuthRoute ? this.loadTranslationsFromJson() : this.loadTranslationsFromApi();
    source$.subscribe(t => this.translations$.next(t));
  }

  loadTranslationsFromJson(): Observable<Map<string, string>> {
    let languageId = this.getLanguageId();

    if (!this.mainLanguages.includes(languageId)) {
      languageId = '1045';
    }

    return this.http.get<{ [key: string]: string }>(`assets/l10n/${languageId}.json`).pipe(
      map(obj => new Map(Object.entries(obj))),
      catchError(err => {
        console.error(`Error loading JSON translations for ${languageId}:`, err);

        return of(new Map());
      })
    );
  }

  private loadTranslationsFromApi(): Observable<Map<string, string>> {
    return this.http.get<{ [key: string]: string }>('/api/L10N/GetL10N').pipe(
      map(obj => new Map(Object.entries(obj))),
      catchError(err => {
        console.error(`Error loading API translations, falling back to JSON:`, err);

        return this.loadTranslationsFromJson();
      })
    );
  }

  private reportMissingTranslation(key: string): void {
    const languageCode = this.getLanguageCode();
    const missingTranslationKey = `${languageCode}:${key}`;

    if (this.missingTranslations.has(missingTranslationKey)) {
      return;
    }

    this.missingTranslations.add(missingTranslationKey);
    this.raygunService.sendError(new Error(`Missing ${languageCode} translation for key: ${key}`));
  }

  private setLanguageCookie(language: string): void {
    const cookieOptions: CookieOptions = {
      expires: 1,
      path: '/',
      secure: true,
      sameSite: 'Strict',
    };
    this.cookieService.set('languageId', language, cookieOptions);
  }
}
